/**
 * Facade hook for offline storage.
 * Composes offlineDb (pure IDB operations) and useSyncQueue (pending operations).
 */
import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/shared/utils/logger';
import { openOfflineDb, dbOperation, DB_CONFIG, putItems, replaceAll } from './offlineDb';
import { useSyncQueue } from './useSyncQueue';

// Re-export types for consumers
export type { OfflineOperationType } from './offlineDb';

/**
 * @description Full IndexedDB-based offline storage with cache management for RA profiles, clothing, shop products/deliveries/returns, laundry, and auto-sync of pending operations.
 * @returns Online/sync status, pending operations queue, cache read/write functions for all modules, and preload metadata management.
 */
export const useOfflineStorageV2 = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [db, setDb] = useState<IDBDatabase | null>(null);

  // Sync queue (pending operations, auto-sync)
  const syncQueue = useSyncQueue({ db, isOnline });

  // Wrapper for dbOperation using current db
  const dbOp = useCallback(<T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> => {
    if (!db) throw new Error('Database not initialized');
    return dbOperation(db, storeName, mode, operation);
  }, [db]);

  // --- Cache: RA Profiles ---
  const cacheRAProfiles = useCallback(async (profiles: any[]) => {
    if (!db) return;
    await putItems(db, DB_CONFIG.stores.raProfiles, profiles);
  }, [db]);

  const getCachedRAProfiles = useCallback(async (): Promise<any[]> => {
    if (!db) return [];
    return dbOp(DB_CONFIG.stores.raProfiles, 'readonly', (store) => store.getAll());
  }, [db, dbOp]);

  // --- Cache: Clothing Requests ---
  const cacheClothingRequests = useCallback(async (requests: any[]) => {
    if (!db) return;
    await putItems(db, DB_CONFIG.stores.clothingRequests, requests);
  }, [db]);

  const getCachedClothingRequests = useCallback(async (): Promise<any[]> => {
    if (!db) return [];
    return dbOp(DB_CONFIG.stores.clothingRequests, 'readonly', (store) => store.getAll());
  }, [db, dbOp]);

  // --- Cache: Shop Products ---
  const cacheShopProducts = useCallback(async (data: { categories: any[], products: any[] }) => {
    if (!db) return;
    try {
      const transaction = db.transaction([DB_CONFIG.stores.shopProducts], 'readwrite');
      const store = transaction.objectStore(DB_CONFIG.stores.shopProducts);

      await new Promise<void>((resolve, reject) => {
        const clearRequest = store.clear();
        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      });

      await new Promise<void>((resolve, reject) => {
        const request = store.put({
          id: 'catalog',
          categories: data.categories,
          products: data.products,
          lastUpdated: Date.now()
        });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('[useOfflineStorageV2] cacheShopProducts failed:', error);
    }
  }, [db]);

  const getCachedShopProducts = useCallback(async (): Promise<{ categories: any[], products: any[] } | null> => {
    if (!db) return null;
    try {
      const result = await dbOp(DB_CONFIG.stores.shopProducts, 'readonly', (store) => store.get('catalog'));
      return result || null;
    } catch {
      return null;
    }
  }, [db, dbOp]);

  // --- Cache: Shop Deliveries ---
  const cacheShopDeliveries = useCallback(async (deliveries: any[]) => {
    if (!db) return;
    await replaceAll(db, DB_CONFIG.stores.shopDeliveries, deliveries);
  }, [db]);

  const getCachedShopDeliveries = useCallback(async (): Promise<any[]> => {
    if (!db) return [];
    return dbOp(DB_CONFIG.stores.shopDeliveries, 'readonly', (store) => store.getAll());
  }, [db, dbOp]);

  // --- Cache: Shop Returns ---
  const cacheShopReturns = useCallback(async (returns: any[]) => {
    if (!db) return;
    await replaceAll(db, DB_CONFIG.stores.shopReturns, returns);
  }, [db]);

  const getCachedShopReturns = useCallback(async (): Promise<any[]> => {
    if (!db) return [];
    return dbOp(DB_CONFIG.stores.shopReturns, 'readonly', (store) => store.getAll());
  }, [db, dbOp]);

  // --- Cache: Laundry Movements ---
  const cacheLaundryMovements = useCallback(async (movements: any[]) => {
    if (!db) return;
    await replaceAll(db, DB_CONFIG.stores.laundryMovements, movements);
  }, [db]);

  const getCachedLaundryMovements = useCallback(async (): Promise<any[]> => {
    if (!db) return [];
    return dbOp(DB_CONFIG.stores.laundryMovements, 'readonly', (store) => store.getAll());
  }, [db, dbOp]);

  // --- Preload Metadata ---
  const updatePreloadMetadata = useCallback(async (metadata: {
    lastPreloadTime: number;
    raProfilesCount: number;
    shopProductsCount: number;
    shopDeliveriesCount: number;
    laundryMovementsCount: number;
  }) => {
    if (!db) return;
    await dbOp(DB_CONFIG.stores.preloadMetadata, 'readwrite', (store) => store.put({ id: 'metadata', ...metadata }));
  }, [db, dbOp]);

  const getPreloadMetadata = useCallback(async () => {
    if (!db) return null;
    try {
      return await dbOp(DB_CONFIG.stores.preloadMetadata, 'readonly', (store) => store.get('metadata'));
    } catch {
      return null;
    }
  }, [db, dbOp]);

  // --- Online/Offline events ---
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      logger.log('Connessione ripristinata');
    };
    const handleOffline = () => {
      setIsOnline(false);
      logger.log('Connessione persa - modalita offline attiva');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // --- Initialize DB ---
  useEffect(() => {
    openOfflineDb()
      .then(database => {
        setDb(database);
        logger.log('Database offline inizializzato');
      })
      .catch(error => {
        logger.error('Errore inizializzazione database:', error);
      });
  }, []);

  // IDB connection lives for the entire SPA session — no cleanup needed.
  // Previous cleanup with [db] dep caused InvalidStateError when multiple
  // hook instances shared the same connection.

  return {
    // Connection status
    isOnline,
    isSyncing: syncQueue.isSyncing,
    lastSyncTime: syncQueue.lastSyncTime,

    // Pending operations
    pendingOperations: syncQueue.pendingOperations,
    pendingCount: syncQueue.pendingCount,
    addPendingOperation: syncQueue.addPendingOperation,
    removePendingOperation: syncQueue.removePendingOperation,
    syncPendingOperations: syncQueue.syncPendingOperations,

    // Cache management - RA Profiles
    cacheRAProfiles,
    getCachedRAProfiles,

    // Cache management - Clothing
    cacheClothingRequests,
    getCachedClothingRequests,

    // Cache management - Shop Products
    cacheShopProducts,
    getCachedShopProducts,

    // Cache management - Shop Deliveries
    cacheShopDeliveries,
    getCachedShopDeliveries,

    // Cache management - Shop Returns
    cacheShopReturns,
    getCachedShopReturns,

    // Cache management - Laundry Movements
    cacheLaundryMovements,
    getCachedLaundryMovements,

    // Preload metadata
    updatePreloadMetadata,
    getPreloadMetadata,

    // Utilities
    isInitialized: !!db,
    clearAllCache: async () => {
      if (!db) return;
      const stores = Object.values(DB_CONFIG.stores);
      for (const storeName of stores) {
        await dbOp(storeName, 'readwrite', (store) => store.clear());
      }
    }
  };
};

export default useOfflineStorageV2;
